import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from "rxjs";
import {ReportService} from "../../../../services/report.service";
import { SharedService } from 'src/app/services/shared.service';
import { EventTypes } from 'src/app/event-types';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { updateReport } from 'src/app/state/search/search.actions';
import { PopupService } from 'src/app/services/popup.service';
@Component({
  selector: 'app-person-contact',
  templateUrl: './person-contact.component.html',
  styleUrls: ['./person-contact.component.scss']
})
export class PersonContactComponent implements  OnInit, OnDestroy {
  emails:any;
  personalEmails:any;
  phoneNumbers:any[] = []; // Initialize to empty array to prevent null errors
  socialProfiles : any;
  loadingPhoneNumbers:any = false;
  reportSubscription: Subscription;
  report: any;

  // Phone "Unblock" flow: phones are no longer retrieved with the report.
  // report.phone_numbers === null -> locked (offer Unblock); [] -> retrieved, none found.
  readonly phoneUnblockCost = 4;
  /** Apollo delivers phones async; give up after this long (matches the backend's pending TTL). */
  readonly phoneUnblockTimeoutMs = 10 * 60 * 1000;
  phoneUnblockConfirmVisible = false;
  /** User clicked Unblock in THIS session (server state covers reloads). */
  phoneUnblockRequested = false;
  private phoneUnblockTimer: ReturnType<typeof setTimeout> | null = null;

  facebookURL: string;
  linkedinURL: string;

  @Output() editContactModalRequested = new EventEmitter<{ emails: string[]; phoneNumbers: string[]; contactType: string }>();

  constructor(
    private reportService: ReportService,
    private sharedService:SharedService,
    private store: Store<AppState>,
    private popupService: PopupService,
    ) {
    this.reportSubscription = reportService.report$.subscribe(
      report => {
        this.report = report;
        this.phoneNumbers = this.report.phone_numbers || [];
        this.personalEmails = this.report.personal_emails;
        // Reload / re-mount mid-retrieval: the backend reports the reveal as
        // pending — restart the local give-up timer so the spinner can never
        // stay stuck if the delivery event is missed.
        if (this.report.phone_unblock_pending) {
          this.startPhoneUnblockTimer();
        }
        this.getSocialProfiles()
      });
  }
  ngOnInit() {

    this.sharedService.eventEmitter.subscribe(event => {
      if (event.type === EventTypes.APOLLO_PHONE_NUMBER) {
        console.log('Event Received:', event.data.phone_numbers);
        const wasRetrieving = this.phoneRetrievalInProgress;
        const previousNumbers = [...this.phoneNumbers];
        this.phoneUnblockRequested = false;
        this.clearPhoneUnblockTimer();
        // Clear the persisted pending flag AND update the component's local
        // report object directly — the store dispatch alone does not flow back
        // into this component's report$ subscription, which is why the
        // Unblock link used to linger under the retrieved number until a
        // full page refresh.
        const updatedReport = {...this.report, phone_numbers: event.data.phone_numbers, phone_unblock_pending: false};
        this.report = updatedReport;
        this.store.dispatch(updateReport({report:updatedReport}))
        if(event.data.phone_numbers){
          this.phoneNumbers = event.data.phone_numbers;
        } else {
          this.phoneNumbers = [];
        }
        // Promised in the "retrieval started" copy: toast the outcome.
        // Billing is on delivery: numbers delivered = charged (even if they
        // are the same as before, on a refresh); nothing delivered = free.
        if (wasRetrieving) {
          const changed = JSON.stringify(previousNumbers) !== JSON.stringify(this.phoneNumbers);
          // Backend flag: did THIS webhook actually deliver numbers? (An empty
          // refresh result keeps the old numbers, so the list alone can't tell.)
          const delivered = event.data.phones_delivered ?? (this.phoneNumbers.length > 0 && changed);
          if (delivered && this.phoneNumbers.length > 0 && previousNumbers.length === 0) {
            this.popupService.notify(
              `Phone number unblocked — ${this.phoneUnblockCost} credits were deducted. You can find it in the Contacts section.`,
              'Phone unblocked', 'success', 6000
            );
          } else if (delivered && changed) {
            this.popupService.notify(
              `Phone numbers refreshed — ${this.phoneUnblockCost} credits were deducted. The new numbers are in the Contacts section.`,
              'Phone refreshed', 'success', 6000
            );
          } else if (delivered && this.phoneNumbers.length > 0) {
            this.popupService.notify(
              `Refresh finished — the retrieval returned the same numbers, so nothing changed. The ${this.phoneUnblockCost} credits were deducted for the delivered retrieval.`,
              'Phone refresh finished', 'info', 6000
            );
          } else if (previousNumbers.length > 0) {
            this.popupService.notify(
              'The refresh brought nothing back — you have not been charged and your existing numbers are kept.',
              'Phone refresh finished', 'info', 6000
            );
          } else {
            this.popupService.notify(
              'No phone number could be found for this person. You have not been charged.',
              'Phone retrieval finished', 'info', 6000
            );
          }
        }
      }
    });

  }

  ngOnDestroy() {
    this.clearPhoneUnblockTimer();
    this.reportSubscription?.unsubscribe();
  }

  /**
   * Cap the retrieval wait: Apollo's phone delivery is asynchronous and not
   * guaranteed. After 10 minutes (same as the backend's pending TTL) stop the
   * spinner and offer the link again. Billing is on delivery only — a timeout
   * means nothing was delivered, so nothing has been charged (first unblock
   * and refresh alike); a late delivery would still store the numbers and
   * bill then.
   */
  private startPhoneUnblockTimer() {
    if (this.phoneUnblockTimer) {
      return; // already counting down
    }

    this.phoneUnblockTimer = setTimeout(() => {
      this.phoneUnblockTimer = null;

      if (!this.phoneRetrievalInProgress) {
        return;
      }

      const wasRefresh = this.phonesRetrieved;
      this.phoneUnblockRequested = false;
      this.store.dispatch(updateReport({report: {...this.report, phone_unblock_pending: false}}));
      this.popupService.notify(
        wasRefresh
          ? 'Phone refresh is taking longer than expected. You have not been charged — if numbers are still delivered, they will appear on the report and the refresh will be charged then.'
          : 'Phone retrieval is taking longer than expected. You have not been charged — if a number is still found, it will appear on the report.',
        wasRefresh ? 'Phone refresh timed out' : 'Phone retrieval timed out', 'warning', 6000
      );
    }, this.phoneUnblockTimeoutMs);
  }

  private clearPhoneUnblockTimer() {
    if (this.phoneUnblockTimer) {
      clearTimeout(this.phoneUnblockTimer);
      this.phoneUnblockTimer = null;
    }
  }

  /**
   * A reveal is in flight: either requested in this session, or reported as
   * pending by the backend (cache marker) — the latter keeps the spinner (not
   * the Unblock/Refresh link) showing after a page reload or component
   * re-mount. Covers both the first unblock and a paid refresh.
   */
  get phoneRetrievalInProgress(): boolean {
    return this.phoneUnblockRequested || this.report?.phone_unblock_pending === true;
  }

  /** Numbers have been retrieved — the link becomes "Refresh phone number". */
  get phonesRetrieved(): boolean {
    return this.phoneNumbers.length > 0;
  }

  /** Phones were never retrieved for this report — locked behind the Unblock link. */
  get phonesLocked(): boolean {
    return this.report?.phone_numbers == null
      && !this.phonesRetrieved
      && !this.phoneRetrievalInProgress;
  }

  requestPhoneUnblock() {
    this.phoneUnblockConfirmVisible = true;
  }

  cancelPhoneUnblock() {
    this.phoneUnblockConfirmVisible = false;
  }

  confirmPhoneUnblock() {
    this.phoneUnblockConfirmVisible = false;
    this.phoneUnblockRequested = true;

    const isRefresh = this.phonesRetrieved;

    this.reportService.unblockPhone(this.report.id, isRefresh).subscribe({
      next: (result) => {
        if (result.data?.status === 'already_unblocked' && result.data.phone_numbers) {
          this.phoneUnblockRequested = false;
          this.clearPhoneUnblockTimer();
          this.phoneNumbers = result.data.phone_numbers;
          const updatedReport = {...this.report, phone_numbers: result.data.phone_numbers, phone_unblock_pending: false};
          this.store.dispatch(updateReport({report: updatedReport}));
          return;
        }
        // 'requested' / 'pending': persist the pending flag on the stored
        // report so the Unblock button cannot reappear (e.g. after the
        // component re-mounts) while we wait for the APOLLO_PHONE_NUMBER
        // websocket event — capped by the give-up timer below.
        this.startPhoneUnblockTimer();
        this.store.dispatch(updateReport({report: {...this.report, phone_unblock_pending: true}}));
        this.popupService.notify(
          isRefresh
            ? `Phone refresh started — ${this.phoneUnblockCost} credits will be deducted when numbers are delivered, even if they come back unchanged. If nothing comes back, you won't be charged. You'll be notified here when it finishes.`
            : `Phone retrieval started — you'll be notified here as soon as the number is unblocked. ${this.phoneUnblockCost} credits will be deducted only when a number is delivered.`,
          isRefresh ? 'Refreshing phone' : 'Unblocking phone', 'success', 5000
        );
      },
      error: (error) => {
        this.phoneUnblockRequested = false;
        this.clearPhoneUnblockTimer();
        const message = error?.error?.message || 'Could not start the phone retrieval. You have not been charged.';
        this.popupService.notify(message, 'Error', 'error');
      }
    });
  }

  getEmails(): string[] {
    const activeCompany = this.getActiveCompany();
    const companyDomain = this.getCompanyDomain(activeCompany);

    // WORK emails (the person's primary Apollo emails) are kept ONLY when they
    // belong to the SELECTED company's domain — so switching the company shows
    // the work email for THAT company, or none, instead of an email tied to a
    // different employer.
    const candidateWorkEmails = [
      this.report?.email,
      this.report?.search_person?.enhanced_data?.email,
    ];
    const companyWorkEmails = candidateWorkEmails.filter(
      (email) => this.emailMatchesDomain(email, companyDomain)
    );

    // Apollo per-role emails for the selected company (employment_history match).
    const employmentEmails = this.getEmploymentEmailsForCompany(activeCompany);

    // PERSONAL emails are kept regardless (company-agnostic contact info).
    const personalEmails: any[] = Array.isArray(this.personalEmails)
      ? this.personalEmails
      : (this.personalEmails ? [this.personalEmails] : []);

    const all = [...companyWorkEmails, ...employmentEmails, ...personalEmails];

    // remove duplicates or null values
    return this.emails = (Array.from(new Set(all)) as string[]).filter(value => value !== "" && value != null);
  }

  /** The report's currently active (switched) company entry, or null. */
  private getActiveCompany(): any {
    const companies = this.report?.search_companies || [];
    return companies.find((c: any) => c.id === this.report?.search_company_id) || null;
  }

  /** Bare lowercase domain for a company (Apollo primary_domain, then website). */
  private getCompanyDomain(company: any): string | null {
    const raw = company?.enhanced_data?.primary_domain || company?.website || null;
    if (!raw) {
      return null;
    }
    let domain = String(raw).trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    return domain || null;
  }

  /** True when `email`'s domain equals (or is a subdomain of) `domain`. */
  private emailMatchesDomain(email: any, domain: string | null): boolean {
    if (!email || !domain) {
      return false;
    }
    const value = String(email).trim().toLowerCase();
    const at = value.lastIndexOf('@');
    if (at < 0) {
      return false;
    }
    const emailDomain = value.slice(at + 1);
    return emailDomain === domain || emailDomain.endsWith('.' + domain);
  }

  /** Per-role Apollo emails for the active company (employment_history match). */
  private getEmploymentEmailsForCompany(company: any): string[] {
    const orgId = company?.apollo_organisation_id;
    const history = this.report?.search_person?.enhanced_data?.employment_history;
    if (!orgId || !Array.isArray(history)) {
      return [];
    }
    const entry = history.find((h: any) => h?.organization_id === orgId);
    const emails = entry?.emails;
    if (Array.isArray(emails)) {
      return emails.filter((e: any) => typeof e === 'string');
    }
    if (typeof emails === 'string') {
      return emails.split(',').map((e: string) => e.trim());
    }
    return [];
  }


  getPhoneNumbers(){
    this.phoneNumbers = this.report.phone_numbers

    return this.phoneNumbers ?? 0;

  }
  getSocialProfiles(): void {
    // console.log('[person-contact.component.ts] getSocialProfiles')

    this.socialProfiles = [this.report.linkedin_profile,this.report.search_person.enhanced_data.facebook_url,this.report.search_person.enhanced_data.github_url,this.report.search_person.enhanced_data.twitter_url]

    this.linkedinURL = this.report.linkedin_profile;
    this.facebookURL = this.report.search_person.enhanced_data.facebook_url;

    // return this.socialProfiles = Array.from(new Set(this.socialProfiles)).filter(value => value !== "" && value !=null)
  }

  isArrayEmpty(array): boolean {
    return Array.isArray(array) && array.length === 0;
  }

  openEditContactModal(): void {
    this.editContactModalRequested.emit({
      emails: this.getEmails(),
      phoneNumbers: this.phoneNumbers || [],
      contactType: 'person'
    });
  }
}
